import { Opportunity, FilterOptions, OpportunityStatus, OpportunityType } from '../types';
import { isValid, parse, isAfter, isBefore, isWithinInterval, addYears, compareAsc } from 'date-fns';

/**
 * Safely parses a date string, returning null if invalid
 * @param dateString The date string to parse
 * @returns A valid Date object or null
 */
const safeParseDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  
  try {
    const date = new Date(dateString);
    return isValid(date) ? date : null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * Determines if a recurring opportunity should be considered active based on its dates
 * @param opportunity The opportunity to evaluate
 * @param now Current date
 * @returns Boolean indicating if the recurring opportunity is active
 */
const isRecurringOpportunityActive = (
  opportunity: Opportunity, 
  now: Date
): boolean => {
  if (!opportunity.isYearlyRecurring) return false;
  
  const startDate = safeParseDate(opportunity.startDate);
  const endDate = safeParseDate(opportunity.endDate);
  
  if (!startDate || !endDate) return false;
  
  // Get month and day components for comparison
  const startMonth = startDate.getMonth();
  const startDay = startDate.getDate();
  const endMonth = endDate.getMonth();
  const endDay = endDate.getDate();
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();
  
  // Simple comparison based on month and day only (ignoring year)
  // This handles recurring events that span the new year (Dec-Jan)
  if (startMonth > endMonth || (startMonth === endMonth && startDay > endDay)) {
    // Event spans the new year (e.g., December to January)
    return (
      (currentMonth > startMonth || (currentMonth === startMonth && currentDay >= startDay)) ||
      (currentMonth < endMonth || (currentMonth === endMonth && currentDay <= endDay))
    );
  } else {
    // Normal case (e.g., January to March)
    return (
      (currentMonth > startMonth || (currentMonth === startMonth && currentDay >= startDay)) &&
      (currentMonth < endMonth || (currentMonth === endMonth && currentDay <= endDay))
    );
  }
};

/**
 * Determines the status of an opportunity based on all date fields
 * @param opportunity The opportunity to evaluate
 * @returns The calculated status
 */
export const calculateOpportunityStatus = (opportunity: Opportunity): OpportunityStatus => {
  const now = new Date();
  
  // Parse all relevant dates
  const startDate = safeParseDate(opportunity.startDate);
  const endDate = safeParseDate(opportunity.endDate);
  const appOpenDate = safeParseDate(opportunity.applicationOpenDate);
  const appCloseDate = safeParseDate(opportunity.applicationCloseDate) || 
                      safeParseDate(opportunity.deadline); // Support legacy deadline field
  
  // Check if this is a recurring opportunity that's currently active
  if (opportunity.isYearlyRecurring && isRecurringOpportunityActive(opportunity, now)) {
    return 'Ongoing';
  }
  
  // Case 1: Program is currently running - always takes precedence
  if (startDate && endDate && 
      isAfter(now, startDate) && 
      isBefore(now, endDate)) {
    return 'Ongoing';
  }
  
  // Case 2: If program has ended, it's always closed
  if (endDate && isAfter(now, endDate)) {
    return 'Closed';
  }

  // Case 3: Application-related status cases
  if (appOpenDate && appCloseDate) {
    // Applications are currently open
    if (isAfter(now, appOpenDate) && isBefore(now, appCloseDate)) {
      return 'Open';
    }
    
    // Applications closed but program hasn't started - this is Coming Soon
    if (isAfter(now, appCloseDate) && startDate && isBefore(now, startDate)) {
      return 'Coming Soon';
    }
  }
  
  // Case 4: Future opportunity logic (explicit Coming Soon handling)
  if (startDate && isBefore(now, startDate)) {
    // If we have application dates and applications haven't started yet
    if (appOpenDate && isBefore(now, appOpenDate)) {
      return 'Coming Soon';
    }
    
    // If we don't have application dates and program is in the future
    if (!appOpenDate && !appCloseDate) {
      const daysUntilStart = Math.ceil((startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      // If program starts within 30 days, mark as Coming Soon, otherwise Open
      return daysUntilStart <= 30 ? 'Coming Soon' : 'Open';
    }
  }

  // Case 5: No application end date but has start date
  if (startDate && !appCloseDate) {
    // Open until start date
    if (isBefore(now, startDate)) {
      return 'Open';
    }
    // After start date but no end date or before end date - ongoing
    if (!endDate || isBefore(now, endDate)) {
      return 'Ongoing';
    }
    // After end date - closed
    return 'Closed';
  }
  
  // Case 6: No application dates at all
  if (!appOpenDate && !appCloseDate) {
    // Open before opportunity starts
    if (startDate && isBefore(now, startDate)) {
      return 'Open';
    }
    // Ongoing while running
    if (startDate && (!endDate || isBefore(now, endDate))) {
      return 'Ongoing';
    }
    // Closed after it ends
    if (endDate && isAfter(now, endDate)) {
      return 'Closed';
    }
  }
  
  // Case 7: Has application deadline but no start date
  if (appCloseDate && !startDate) {
    // Open until application deadline
    if (isBefore(now, appCloseDate)) {
      return 'Open';
    } 
    // Ongoing after deadline (since we don't know when it actually starts)
    if (!endDate || isBefore(now, endDate)) {
      return 'Ongoing';
    }
    // Closed after any end date
    return 'Closed';
  }
  
  // Default to the status in the database if calculation is inconclusive
  // Ensure we always return a valid status, defaulting to 'Open' if status is undefined
  return opportunity.status || 'Open';
};

/**
 * Gets days remaining until deadline with improved error handling
 * @param dateString The date to calculate days until
 * @returns String with formatted days remaining
 */
export const getDaysUntilDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  
  try {
    const targetDate = safeParseDate(dateString);
    if (!targetDate) return null;
    
    const today = new Date();
    
    // Set both dates to midnight to get accurate day difference
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);
    
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Deadline passed';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays <= 7) return `${diffDays} days left`;
    if (diffDays <= 30) return 'Due this month';
    return 'Future deadline';
  } catch (error) {
    console.error('Error calculating days until date:', error);
    return null;
  }
};

/**
 * Gets the most relevant deadline date for an opportunity
 * Priority: applicationCloseDate > deadline > endDate
 */
export const getMostRelevantDeadlineDate = (opportunity: Opportunity): string | null => {
  if (opportunity.applicationCloseDate) return opportunity.applicationCloseDate;
  if (opportunity.deadline) return opportunity.deadline;
  if (opportunity.endDate) return opportunity.endDate;
  return null;
};

/**
 * Filter and sort opportunities based on provided filter criteria
 */
export const filterOpportunities = (
  opportunities: Opportunity[],
  filters: FilterOptions
): Opportunity[] => {
  // Update status of all opportunities before filtering
  const updatedOpportunities = opportunities.map(opp => ({
    ...opp,
    status: calculateOpportunityStatus(opp)
  }));
  
  // First apply filters
  const filteredOpportunities = updatedOpportunities.filter((opportunity) => {
    // Filter by search term
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase().trim();
      console.log('🔍 Filtering opportunity:', opportunity.title, 'with search term:', searchTerm);
      
      const matchesSearch =
        opportunity.title.toLowerCase().includes(searchTerm) ||
        opportunity.description.toLowerCase().includes(searchTerm) ||
        opportunity.organization.toLowerCase().includes(searchTerm) ||
        opportunity.location.toLowerCase().includes(searchTerm) ||
        (opportunity.eligibility && opportunity.eligibility.toLowerCase().includes(searchTerm)) ||
        opportunity.tags.some((tag) => tag.toLowerCase().includes(searchTerm));
      
      console.log('🔍 Opportunity matches search:', matchesSearch);
      if (!matchesSearch) return false;
    }
    
    // Filter by type
    if (filters.type && filters.type.length > 0) {
      // Handle both array and comma-separated string types
      let opportunityTypes: string[] = [];
      
      if (Array.isArray(opportunity.type)) {
        opportunityTypes = opportunity.type;
      } else if (typeof opportunity.type === 'string') {
        // Split comma-separated types into array
        opportunityTypes = opportunity.type.split(',').map(t => t.trim());
      }
      
      // Check if any of the opportunity's types match any of the filter types
      const hasMatchingType = opportunityTypes.some(type => 
        filters.type!.includes(type as OpportunityType)
      );
      
      if (!hasMatchingType) return false;
    }
    
    // Filter by status (now handling array of statuses)
    if (filters.status && filters.status.length > 0) {
      // Check if the opportunity's status is in the selected statuses array
      if (!filters.status.includes(opportunity.status)) return false;
    }
    
    // Filter by tags
    if (filters.tags && filters.tags.length > 0) {
      const hasTags = filters.tags.some((tag) => 
        opportunity.tags.includes(tag)
      );
      
      if (!hasTags) return false;
    }
    
    // Filter by grade level
    if (filters.gradeLevel && filters.gradeLevel.length > 0) {
      if (!opportunity.gradeLevel || opportunity.gradeLevel.length === 0) return false;
      
      const hasMatchingGradeLevel = filters.gradeLevel.some(grade => 
        opportunity.gradeLevel?.includes(grade) || opportunity.gradeLevel?.includes('All Levels')
      );
      
      if (!hasMatchingGradeLevel) return false;
    }
    
    // Filter by interaction type (in-person, virtual, hybrid)
    if (filters.interactionType && filters.interactionType.length > 0) {
      if (!opportunity.interactionTypes || opportunity.interactionTypes.length === 0) return false;
      
      const hasMatchingInteractionType = filters.interactionType.some(type => 
        opportunity.interactionTypes?.includes(type)
      );
      
      if (!hasMatchingInteractionType) return false;
    }
    
    return true;
  });

  // Then apply sorting
  if (filters.sortBy) {
    return filteredOpportunities.sort((a, b) => {
      switch (filters.sortBy) {
        case 'deadline-asc': {
          const deadlineA = getMostRelevantDeadlineDate(a);
          const deadlineB = getMostRelevantDeadlineDate(b);
          
          // Handle null dates by placing them at the end
          if (!deadlineA && !deadlineB) return 0;
          if (!deadlineA) return 1;
          if (!deadlineB) return -1;
          
          const dateA = safeParseDate(deadlineA);
          const dateB = safeParseDate(deadlineB);
          
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          
          return compareAsc(dateA, dateB);
        }
        
        case 'date-asc': {
          const startDateA = safeParseDate(a.startDate);
          const startDateB = safeParseDate(b.startDate);
          
          // Handle null dates by placing them at the end
          if (!startDateA && !startDateB) return 0;
          if (!startDateA) return 1;
          if (!startDateB) return -1;
          
          return compareAsc(startDateA, startDateB);
        }
        
        case 'alpha':
          return a.title.localeCompare(b.title);
        
        default:
          return 0;
      }
    });
  }
  
  // Default sorting: Open first, then Coming Soon, then Ongoing, then Closed
  const statusPriority: Record<OpportunityStatus, number> = {
    'Open': 1,
    'Coming Soon': 2,
    'Ongoing': 3,
    'Closed': 4
  };

  return filteredOpportunities.sort((a, b) => {
    // First sort by status priority
    const statusOrderA = statusPriority[a.status] || 5; // Default to lowest priority if status is unknown
    const statusOrderB = statusPriority[b.status] || 5;
    
    if (statusOrderA !== statusOrderB) {
      return statusOrderA - statusOrderB;
    }
    
    // If same status, sort by oldest created date (secondary sort)
    const createdAtA = new Date(a.createdAt || 0).getTime();
    const createdAtB = new Date(b.createdAt || 0).getTime();
    return createdAtA - createdAtB; // Ascending order for createdAt (older first)
  });
};